import { useReactiveVar } from '@apollo/client';
import dynamic from 'next/dynamic';
import { Banner, Box, List, Paragraph, Text, TextButton } from '@zillow/constellation-10';
import { MESSAGE_LOAD_ERROR_TEXT, MESSAGE_TYPES, NewMessageNotificationBanner } from '@zg-rentals/satellite-components';
import { format, isSameDay, isThisYear, isToday, isYesterday } from 'date-fns';
import React, { forwardRef, useCallback, useContext, useEffect, useRef, useState } from 'react';
import smoothscroll from 'smoothscroll-polyfill';
import {
  MessageList_FetchPaginatedMessagesDocument,
  useMessageList_GetConversationQuery,
} from '../../types/GraphqlTypes';
import { useQueryClient } from '@zg-rentals/react-query5';
import { useSatelliteWebLandlordConversationHandler_getAttachmentsMetadata } from '@zg-rentals/ts-satellite-client-react-query';
import { ModalType } from '../../types/ModalType';
import { preventMessageListScrollVariable, shouldRefetchMessagesVariable } from '../../lib/apollo';
import useModal from '../../hooks/useModal';
import { AppContext } from '../../contexts/AppContextProvider';
import { isUserAuthError, logApolloError } from '../../utils/errorUtils';
import stringUtils from '../../utils/stringUtils';
import viewUtils from '../../utils/viewUtils';
import isValidListingAlias from '../../utils/isValidListingAlias';
import isValidConversationId from '../../utils/isValidConversationId';
import type { AttachmentsForMessage } from '../MessageAttachments';
import MessageItem, { type OnSystemMessageCtaClick } from '../MessageItem';
import DeleteAttachments from '../Modals/DeleteAttachments';
import RenterProfileChatBubble from '../RenterProfileChatBubble';
import EmptyContainer from '../MessageList/components/EmptyContainer';
import { MessageListContentsSkeleton } from './skeleton';
import ErrorContainer from './components/ErrorContainer';
import useFeatureFlagsHelper from '../../hooks/useFeatureFlagsHelper';
import { TRIGGER_LOCATION, type TriggerLocation } from '../../constants/clickstreamEvents/globals';
import { appMarkerLogger, bootstrap } from '../../bootstrap';
import { scrollbarStyles } from '../../styles/scrollbar';

const logger = appMarkerLogger.child({ name: 'MessageListItems' });

export type ConversationIdentity = {
  clientTimeZone: string;
  conversationId: string;
  listingAlias: string | undefined;
};

export type OnResendMessageComplete = (args: { conversation: ConversationIdentity }) => void;

type OpenDeleteAttachmentsModalArgs = {
  attachments: AttachmentsForMessage;
  messageId: string;
};

type OpenImagesViewerArgs = {
  imageAttachments: AttachmentsForMessage;
  selectedImageIndex: number;
};

export type HandleOpenDeleteAttachmentsModal = ({ attachments, messageId }: OpenDeleteAttachmentsModalArgs) => void;
export type HandleOpenImagesViewer = ({ imageAttachments, selectedImageIndex }: OpenImagesViewerArgs) => void;

const ImagesViewer = dynamic(() => import('../ImagesViewer'), {
  loading: () => null,
  ssr: false,
});

const POLL_INTERVAL_IN_MS = 60 * 1000;
const DATE_FNS_FORMAT = 'EEEE, MMM do';
const DATE_FNS_FORMAT_WITH_YEAR = 'MMM do, yyyy';
const START_CONVERSATION_TEXT = 'This is the start of your conversation';

// Polyfill smooth scroll for safari :(
if (typeof window !== 'undefined' && window !== null) {
  smoothscroll.polyfill();
}

export type MessageListItemsProps = {
  conversation: ConversationIdentity;
  showEmptyContainer?: boolean;
  shouldRefetchAttachments?: boolean;
  showMoveToInboxBanner?: boolean;
  onResendMessageComplete?: OnResendMessageComplete;
  onConversationChange?: () => void;
  onRenterProfileClick: () => void;
  onSystemMessageCtaClick: OnSystemMessageCtaClick;
  onScrollComplete: () => void;
  triggerLocation: TriggerLocation;
};

const MessageListItems = forwardRef<HTMLDivElement, MessageListItemsProps>(function MessageListItems(
  {
    conversation,
    showEmptyContainer = false,
    shouldRefetchAttachments = false,
    showMoveToInboxBanner = false,
    onResendMessageComplete,
    onConversationChange,
    onRenterProfileClick,
    onSystemMessageCtaClick,
    onScrollComplete,
    triggerLocation,
  },
  ref,
) {
  const { conversationId, listingAlias, clientTimeZone } = conversation;
  const queryClient = useQueryClient();
  const shouldRefetchMessages = useReactiveVar(shouldRefetchMessagesVariable);
  const preventMessageListScroll = useReactiveVar(preventMessageListScrollVariable);

  // Refs
  const messageListEndRef = useRef<HTMLDivElement | null>(null);
  const currentConversationId = useRef<string | undefined>('');

  // State
  const [prevMessageCount, setPrevMessageCount] = useState<number | null>(null);
  const [showPaginationLoading, setShowPaginationLoading] = useState(false);
  const [showNewMessageNotification, setShowNewMessageNotification] = useState(false);
  const [prevFullMessageCount, setPrevFullMessageCount] = useState<number>(0);
  const [imagesViewerData, setImagesViewerData] = useState<{
    imageAttachments: AttachmentsForMessage;
    selectedImageIndex: number;
  } | null>(null);
  const [deleteAttachmentsModalData, setDeleteAttachmentsModalData] = useState<{
    attachments: AttachmentsForMessage;
    messageId: string;
  } | null>(null);
  const [hasAuthError, setHasAuthError] = useState(false);
  const { setHasMessageListError } = useContext(AppContext);
  const { activeModal, setActiveModal } = useModal();
  const { enableWebsocketIntegration } = useFeatureFlagsHelper();

  const scrollToBottom = useCallback(() => {
    if (messageListEndRef.current) {
      messageListEndRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
        inline: 'end',
      });
    }
    setShowNewMessageNotification(false);
    onScrollComplete();
  }, [onScrollComplete]);

  // check if the user has scrolled to the bottom of the message list
  const isMessagesEndInViewportBeforePoll = viewUtils.isElementInViewport(messageListEndRef.current);

  const {
    data: messageListData,
    loading,
    error,
    fetchMore,
    refetch,
    startPolling,
    stopPolling,
  } = useMessageList_GetConversationQuery({
    variables: {
      conversationId: conversationId || '',
      listingAlias: listingAlias || '',
      clientTimeZone,
    },
    fetchPolicy: 'cache-first',
    pollInterval: enableWebsocketIntegration ? 0 : POLL_INTERVAL_IN_MS,
    ssr: false,
    onCompleted(data) {
      if (prevMessageCount === null) {
        setPrevMessageCount(Number(data.ZRM_getConversationById.pagination?.pageConversationCount));
        setPrevFullMessageCount(Number(data.ZRM_getConversationById.pagination?.queryFullMessageCount));
      }
      // Did conversation change
      if (currentConversationId.current != conversationId) {
        // Scroll to bottom
        // Do that here instead of on a useEffect so we only scroll after new data is finished loading
        setPrevMessageCount(Number(data.ZRM_getConversationById.pagination?.pageConversationCount));
        currentConversationId.current = conversationId;
        setTimeout(scrollToBottom, 500);
      } else {
        const currentFullMessageCount = Number(data.ZRM_getConversationById.pagination?.queryFullMessageCount);
        const { hasUnreadMessage } = data.ZRM_getConversationById;

        if (hasUnreadMessage) {
          const shouldScrollToMessagesEnd =
            isMessagesEndInViewportBeforePoll && !showPaginationLoading && !preventMessageListScroll;

          const shouldShowNewMessageNotification =
            !isMessagesEndInViewportBeforePoll && !showNewMessageNotification && !showPaginationLoading;

          // If the user was already scrolled to the bottom before the new message came in,
          // auto scroll to the bottom to keep the new message in view
          if (shouldScrollToMessagesEnd) {
            setTimeout(scrollToBottom, 500);
          } else if (shouldShowNewMessageNotification) {
            // If the user was not scrolled to the bottom, show the new message notification banner
            setShowNewMessageNotification(true);
          }
        } else if (!preventMessageListScroll && !showPaginationLoading) {
          // Scroll to bottom of message list when hasUnreadMessage is false
          // (i.e. sending a new message & polling for new messages)
          setTimeout(scrollToBottom, 500);
        }

        // Invalidate getAttachmentsMetadata when new messages contain attachments
        // (only when shouldRefetchAttachments is true, e.g. inbox page)
        if (shouldRefetchAttachments && currentFullMessageCount && prevFullMessageCount !== currentFullMessageCount) {
          setPrevFullMessageCount(currentFullMessageCount);
          const { conversation } = data.ZRM_getConversationById;
          const newMessageStartIndex = Math.max(0, conversation.length - 1);
          const newMessageEndIndex = Math.max(
            0,
            newMessageStartIndex - (currentFullMessageCount - prevFullMessageCount) + 1,
          );
          for (let i = newMessageStartIndex; i >= newMessageEndIndex; i--) {
            const { attachments } = conversation[i];
            if (attachments.length) {
              queryClient.invalidateQueries({
                queryKey: useSatelliteWebLandlordConversationHandler_getAttachmentsMetadata.getKey({
                  conversationId,
                }),
              });
              break;
            }
          }
        } else if (currentFullMessageCount && prevFullMessageCount !== currentFullMessageCount) {
          setPrevFullMessageCount(currentFullMessageCount);
        }
      }

      // Reset pagination loading messaging
      if (showPaginationLoading) setShowPaginationLoading(false);
      // Reset preventMessageListScroll when it was set by a consumer (e.g. DeleteAttachments modal)
      if (preventMessageListScroll) {
        preventMessageListScrollVariable(false);
      }
      // Update error state to false to allow other components to render
      setHasMessageListError(false);
      setHasAuthError(false);
    },
    onError(err) {
      if (isUserAuthError(err)) {
        bootstrap.monitor.count({ name: 'User not authorized to view conversation' });
        logger.info('User not authorized to view conversation - viewing authorization error message', {
          conversationId,
          listingAlias,
        });

        setHasMessageListError(true);
        setHasAuthError(true);
      } else {
        setHasMessageListError(true);
        setHasAuthError(false);
        logApolloError(err, {
          details: 'MessageList - Error fetching messages',
          conversationId: conversationId || '',
          listingAlias: listingAlias || '',
          clientTimeZone,
          isValidListingAlias: isValidListingAlias(listingAlias || ''),
          isValidConversationId: isValidConversationId(conversationId || ''),
        });
      }

      // In the future we might want to show offline status
    },
  });

  useEffect(() => {
    if (showPaginationLoading) setShowPaginationLoading(false);
    if (showNewMessageNotification) setShowNewMessageNotification(false);
    onConversationChange?.();
    // Only run when conversationId changes; onConversationChange is intentionally excluded to avoid extra runs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // refetches the message list when receiving a new conversation through websocket that matches the currently viewing one
  useEffect(() => {
    if (enableWebsocketIntegration && shouldRefetchMessages) {
      refetch();
      // Reset the reactive variable after refetching
      shouldRefetchMessagesVariable(false);
    }
  }, [refetch, enableWebsocketIntegration, shouldRefetchMessages]);

  // If value of feature flag enableWebsocketIntegration changes, update the polling accordingly
  useEffect(() => {
    if (enableWebsocketIntegration) {
      stopPolling();
    } else {
      startPolling(POLL_INTERVAL_IN_MS);
    }
    // Cleanup function to stop polling when the component unmounts or the value changes
    return () => stopPolling();
  }, [enableWebsocketIntegration, startPolling, stopPolling, POLL_INTERVAL_IN_MS]);

  const handleOpenDeleteAttachmentsModal: HandleOpenDeleteAttachmentsModal = useCallback(
    ({ attachments, messageId }) => {
      setDeleteAttachmentsModalData({
        attachments,
        messageId,
      });
      setActiveModal(ModalType.DeleteAttachmentsModal);
    },
    [],
  );

  const handleOpenImagesViewer: HandleOpenImagesViewer = useCallback(({ imageAttachments, selectedImageIndex }) => {
    setImagesViewerData({
      imageAttachments,
      selectedImageIndex,
    });
    setActiveModal(ModalType.ImagesViewer);
  }, []);

  const handleSystemMessageCtaClick: OnSystemMessageCtaClick = useCallback(
    ({ messageType, url }) => {
      onSystemMessageCtaClick({ messageType, url });
    },
    [onSystemMessageCtaClick],
  );

  if (!messageListData && error) {
    return <ErrorContainer hasAuthError={hasAuthError} />;
  }

  if (showEmptyContainer) {
    return <EmptyContainer />;
  }

  const handleLoadMoreMessages = async () => {
    setShowPaginationLoading(true);
    const response = await fetchMore({
      query: MessageList_FetchPaginatedMessagesDocument,
      variables: {
        conversationId: conversationId || '',
        listingAlias: listingAlias || '',
        paginationStartIndex: messageListData?.ZRM_getConversationById.conversation?.[0]?.messageId ?? null,
        clientTimeZone,
      },
    });
    setPrevMessageCount(Number(response.data.ZRM_getConversationById.pagination?.pageConversationCount));
  };

  const handleCloseNewMessageNotification = () => {
    setShowNewMessageNotification(false);
  };

  let prevMessageDateMs: string;
  const conversationMessages = messageListData?.ZRM_getConversationById.conversation || [];
  const { pagination } = messageListData?.ZRM_getConversationById || {};
  const currentPageConversationCount = Number(pagination?.pageConversationCount) || null;

  return (
    <>
      {activeModal === ModalType.ImagesViewer && (
        <ImagesViewer
          imageAttachments={imagesViewerData?.imageAttachments ?? []}
          selectedImageIndex={imagesViewerData?.selectedImageIndex || 0}
          triggerLocation={triggerLocation}
        />
      )}

      {activeModal === ModalType.DeleteAttachmentsModal && (
        <DeleteAttachments
          attachments={deleteAttachmentsModalData?.attachments || []}
          messageId={deleteAttachmentsModalData?.messageId || ''}
          conversationId={conversationId}
          listingAlias={listingAlias ?? ''}
          clientTimeZone={clientTimeZone}
        />
      )}

      {showNewMessageNotification && (
        <NewMessageNotificationBanner onActionClick={scrollToBottom} onClose={handleCloseNewMessageNotification} />
      )}

      {showMoveToInboxBanner && (
        <Banner tone="info">To reply to this renter, tap Move to inbox to move the conversation to your inbox.</Banner>
      )}

      <Box
        ref={ref}
        css={{
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          paddingLeft: 'default',
          paddingRight: 'tight',
          flex: 1,
          width: '100%',
          overscrollBehavior: 'contain',
          ...scrollbarStyles,
        }}
      >
        {!messageListData && loading ? (
          <MessageListContentsSkeleton />
        ) : (
          <>
            {showPaginationLoading && (
              <TextButton disabled css={{ margin: 'default' }}>
                Loading more messages...
              </TextButton>
            )}

            {!showPaginationLoading && currentPageConversationCount !== null && currentPageConversationCount >= 10 && (
              <TextButton onClick={handleLoadMoreMessages} css={{ margin: 'default' }}>
                Load more messages
              </TextButton>
            )}

            {!showPaginationLoading && currentPageConversationCount !== null && currentPageConversationCount < 10 && (
              <Paragraph
                textStyle="body-sm-bold"
                css={{ color: 'text.subtle', textAlign: 'center', marginTop: 'default', marginBottom: 'loose' }}
              >
                {START_CONVERSATION_TEXT}
              </Paragraph>
            )}

            <List.Root appearance="none" css={{ marginBottom: 'loose' }}>
              {conversationMessages.map((conversationMessage, idx) => {
                let message = conversationMessage;
                let timestamp;
                const isLastMessageInList = idx === conversationMessages.length - 1;
                const { attachments, message: messageText, messageDateMs, messageId } = message;
                const isMessageSameDayAsPrevious = isSameDay(+messageDateMs, +prevMessageDateMs);
                const key = `${messageId}-${messageDateMs}`;
                prevMessageDateMs = messageDateMs;

                if (messageDateMs && !isMessageSameDayAsPrevious) {
                  if (isYesterday(+messageDateMs)) {
                    timestamp = 'Yesterday';
                  } else if (isToday(+messageDateMs)) {
                    timestamp = 'Today';
                  } else {
                    const fnsFormat = isThisYear(new Date(+messageDateMs))
                      ? DATE_FNS_FORMAT
                      : DATE_FNS_FORMAT_WITH_YEAR;
                    timestamp = format(new Date(+messageDateMs), fnsFormat);
                  }
                }

                // Edge case: message objects with missing message AND attachments fields - return "failed to load" item
                if (!messageText && !attachments) {
                  message = {
                    ...message,
                    message: MESSAGE_LOAD_ERROR_TEXT,
                    messageType: MESSAGE_TYPES.SYSTEM_MESSAGE.FAILED_TO_LOAD,
                    uiTreatmentType: MESSAGE_TYPES.LOW_PRIORITY,
                  };

                  return (
                    <MessageItem
                      conversation={conversation}
                      isLastMessageInList={isLastMessageInList}
                      key={key}
                      message={message}
                      onResendMessageComplete={onResendMessageComplete}
                      onSystemMessageCtaClick={handleSystemMessageCtaClick}
                      openDeleteAttachmentsModal={handleOpenDeleteAttachmentsModal}
                      openImagesViewer={handleOpenImagesViewer}
                      triggerLocation={triggerLocation}
                    />
                  );
                }

                // Edge case: message field with no text AND no attachments - do not render
                if (stringUtils.isEmptyString(messageText || '') && attachments.length === 0) {
                  return null;
                }

                return (
                  <React.Fragment key={key}>
                    {timestamp && (
                      <Text
                        asChild
                        textStyle="body-xs"
                        css={{
                          display: 'block',
                          color: 'text.subtle',
                          marginBlock: 'default',
                          textAlign: 'center',
                        }}
                      >
                        <time>{timestamp}</time>
                      </Text>
                    )}
                    {idx === 0 && (currentPageConversationCount || 0) < 10 && (
                      <RenterProfileChatBubble
                        conversationId={conversationId}
                        listingAlias={listingAlias}
                        onRenterProfileClick={onRenterProfileClick}
                        shouldShowViewRenterProfileButton={triggerLocation === TRIGGER_LOCATION.LeadManagementPage}
                      />
                    )}
                    <MessageItem
                      conversation={conversation}
                      isLastMessageInList={isLastMessageInList}
                      message={message}
                      onResendMessageComplete={onResendMessageComplete}
                      onSystemMessageCtaClick={handleSystemMessageCtaClick}
                      openDeleteAttachmentsModal={handleOpenDeleteAttachmentsModal}
                      openImagesViewer={handleOpenImagesViewer}
                      triggerLocation={triggerLocation}
                    />
                  </React.Fragment>
                );
              })}
            </List.Root>
          </>
        )}
        <Box ref={messageListEndRef} css={{ paddingBottom: 'loose' }} />
      </Box>
    </>
  );
});

export default MessageListItems;
